import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Accordion — Expandable section with gold left border, chevron animation,
 * and smooth height transition. Part of the ProRecruit Executive design system.
 */
const Accordion = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, children]);

  return (
    <div
      className={`border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 ${
        isOpen ? 'border-l-4 border-l-[#A47713] shadow-sm' : ''
      }`}
    >
      <button
        type="button"
        className={`w-full flex items-center justify-between py-3 px-4 text-left font-semibold text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#A47713]/30 ${
          isOpen
            ? 'text-[#0077B6] bg-slate-50'
            : 'text-slate-700 bg-white hover:bg-slate-50'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className="transition-all duration-300 ease-in-out overflow-hidden"
        style={{ maxHeight: isOpen ? `${contentHeight + 32}px` : '0px' }}
      >
        <div ref={contentRef} className="p-4 text-sm">
          {children}
        </div>
      </div>
    </div>
  );
};

Accordion.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
};

export default Accordion;
